import { useEffect, useMemo, useState } from 'react'
import type { default as LeafletDefault } from 'leaflet'
import type {
  MapContainer as MapContainerType,
  Marker as MarkerType,
  TileLayer as TileLayerType,
  useMap as useMapType,
} from 'react-leaflet'
import { useTheme } from '@/lib/theme'

// Leaflet imports - only used in client component
let L: typeof LeafletDefault | undefined
let MapContainer: typeof MapContainerType | undefined
let TileLayer: typeof TileLayerType | undefined
let Marker: typeof MarkerType | undefined
let useMap: typeof useMapType | undefined

const initLeaflet = async () => {
  if (typeof window === 'undefined') return

  const leafletModule = await import('leaflet')
  const reactLeafletModule = await import('react-leaflet')
  await import('leaflet/dist/leaflet.css')

  L = leafletModule.default
  MapContainer = reactLeafletModule.MapContainer
  TileLayer = reactLeafletModule.TileLayer
  Marker = reactLeafletModule.Marker
  useMap = reactLeafletModule.useMap
}

// Car icon - green teardrop
const createCarIcon = (color: string = '#10b981') => {
  if (!L) return undefined
  return L.divIcon({
    html: `
      <div style="
        width: 24px;
        height: 24px;
        background-color: ${color};
        border: 3px solid white;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        position: relative;
      ">
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(45deg);
          width: 8px;
          height: 8px;
          background-color: white;
          border-radius: 50%;
        "></div>
      </div>
    `,
    className: 'car-marker',
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  })
}

// User location icon - blue pulsing dot
const createUserLocationIcon = () => {
  if (!L) return undefined
  return L.divIcon({
    html: `
      <div style="position: relative; width: 20px; height: 20px;">
        <div style="
          position: absolute;
          width: 20px;
          height: 20px;
          background-color: #3b82f6;
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          z-index: 2;
        "></div>
        <div style="
          position: absolute;
          width: 20px;
          height: 20px;
          background-color: rgba(59, 130, 246, 0.3);
          border-radius: 50%;
          animation: pulse 2s ease-out infinite;
        "></div>
      </div>
      <style>
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(2.5); opacity: 0; }
        }
      </style>
    `,
    className: 'user-location-marker',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  })
}

export function openDirections(lat: number, lng: number) {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  const url = isIOS
    ? `maps://maps.apple.com/?daddr=${lat},${lng}&dirflg=w`
    : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=walking`
  window.open(url, '_blank')
}

interface RentalLocationMapProps {
  carLocation: { latitude: number; longitude: number }
  carName: string
}

// FitBounds child component - uses useMap() inside MapContainer
function FitBounds({
  carLocation,
  userLocation,
}: {
  carLocation: [number, number]
  userLocation: [number, number] | null
}) {
  const map = useMap!()

  useEffect(() => {
    if (userLocation) {
      const bounds = L!.latLngBounds([carLocation, userLocation])
      map.fitBounds(bounds, { padding: [50, 50] })
    } else {
      map.setView(carLocation, 15)
    }
  }, [map, carLocation, userLocation])

  return null
}

export function RentalLocationMap({
  carLocation,
  carName,
}: RentalLocationMapProps) {
  const [leafletLoaded, setLeafletLoaded] = useState(false)
  const [userLocation, setUserLocation] = useState<[number, number] | null>(
    null,
  )
  const { resolvedTheme } = useTheme()

  useEffect(() => {
    if (typeof window === 'undefined') return
    initLeaflet().then(() => setLeafletLoaded(true))
  }, [])

  // Get user location
  useEffect(() => {
    if (typeof window === 'undefined') return
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation([position.coords.latitude, position.coords.longitude])
      },
      () => {
        // Silently fail - we'll just show the car location
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    )
  }, [])

  const tileLayerConfig = useMemo(() => {
    if (resolvedTheme === 'dark') {
      return {
        url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      }
    }
    return {
      url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    }
  }, [resolvedTheme])

  const carLatLng: [number, number] = [
    carLocation.latitude,
    carLocation.longitude,
  ]

  if (!leafletLoaded || !MapContainer || !TileLayer || !Marker) {
    return (
      <div
        className="h-48 rounded-lg overflow-hidden bg-muted animate-pulse"
        data-testid="map-loading"
      />
    )
  }

  return (
    <div className="h-48 rounded-lg overflow-hidden" data-testid="rental-map">
      <MapContainer
        center={carLatLng}
        zoom={15}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer
          key={resolvedTheme}
          attribution={tileLayerConfig.attribution}
          url={tileLayerConfig.url}
        />
        <Marker
          position={carLatLng}
          icon={createCarIcon('#10b981')}
          title={carName}
        />
        {userLocation && (
          <Marker
            position={userLocation}
            icon={createUserLocationIcon()}
            zIndexOffset={1000}
          />
        )}
        <FitBounds carLocation={carLatLng} userLocation={userLocation} />
      </MapContainer>
    </div>
  )
}
